// @ts-nocheck
import * as Sentry from "@sentry/react";
const { logger } = Sentry;

/**
 * Turns whatever a caller handed us into something Sentry can display. Error instances are captured as-is (so the
 * message and stack survive). Strings become an Error with that message. Anything else (plain objects, Amplify's
 * `{ data, errors }` rejection objects, undefined) is stringified into the message and also attached as an extra,
 * so the event title is never "{}" or "<unknown>".
 */
function toCapturableError(error: any, msg: string): Error {
	if (error instanceof Error) return error;
	if (typeof error === "string") return new Error(error);
	let serialized = "";
	try {
		serialized = JSON.stringify(error);
	} catch {
		serialized = String(error);
	}
	return new Error(`${msg} ${serialized ?? String(error)}`.trim());
}

export const recordSentry = ({ error, msg, showModal, additionalData }: { error: any; msg?: string; showModal?: boolean; additionalData?: any }) => {
	if (!msg) msg = "Error: ";

	Sentry.withScope((scope) => {
		scope.setTag("modal", showModal ? "true" : "false");
		scope.setTransactionName(msg);
		scope.setExtra("msg", msg);
		if (additionalData) scope.setExtra("additionalData", additionalData);
		if (!(error instanceof Error)) scope.setExtra("rawError", error);
		Sentry.captureException(toCapturableError(error, msg));
	});
};

/**
 * Leaves a breadcrumb (and a warning-level message) whenever the app writes documents to repair or complete data on
 * the user's behalf. These are the writes that have historically produced duplicate or orphaned field instances, so
 * every one of them should be traceable after the fact.
 * @param category A short identifier for the repair path, e.g. "verifyFormGroupInstances"
 * @param data Ids and counts that let someone reconstruct what was written
 */
export const recordRepairBreadcrumb = (category: string, data: Record<string, unknown>) => {
	Sentry.addBreadcrumb({ category: `data-repair.${category}`, level: "warning", message: category, data });
	Sentry.withScope((scope) => {
		scope.setLevel("warning");
		scope.setTag("data-repair", category);
		for (const [key, value] of Object.entries(data)) {
			scope.setExtra(key, value);
		}
		Sentry.captureMessage(`data-repair: ${category}`);
	});
};

export const logSentry = (msg: string, additionalData?: any) => {
	if (logger) {
		logger.info(logger.fmt`${msg}.`);
	}
};
